import { createContext, useCallback, useContext, useRef, useState } from 'react';

export type Toast = {
    message: string;
    type?: 'success' | 'error';
    duration?: number;
};

const ToastContext = createContext<{ toast: (toast: Toast) => void } | null>(null);

export const useToast = () => {
    const toastContext = useContext(ToastContext);
    if (toastContext === null) {
        throw Error('ToasContext hs not been provided');
    }
    return toastContext;
};

export const ToastProvider = ({ children }) => {
    const [toast, setToast] = useState<Toast | null>(null);
    const toastTimeout = useRef<NodeJS.Timeout>();

    const toastCallback = useCallback<(newToast: Toast) => void>(
        (newToast) => {
            if (toastTimeout.current) clearTimeout(toastTimeout.current);
            toastTimeout.current = setTimeout(() => setToast(null), newToast.duration || 3000);
            setToast(newToast);
        },
        [setToast],
    );

    return (
        <ToastContext.Provider value={{ toast: toastCallback }}>
            {children}
            <ToastView toast={toast} />
        </ToastContext.Provider>
    );
};

interface ToastProps {
    toast: Toast | null;
}

// TODO REIS-5295: Copied from warp, replace with react component if available.
const ToastView = ({ toast }: ToastProps) => {
    const isError = toast?.type === 'error';
    const role = isError ? 'alert' : 'status';
    const ariaLabel = isError ? 'Feil' : 'Vellykket';
    const containerClass = isError ? 'bg-red-50 border-red-200 text-red-800' : 'bg-green-50 border-green-200 text-green-800';
    const iconClass = isError ? 'bg-red-300' : 'bg-green-300';

    return toast ? (
        <aside className="fixed fixed-ios-fix bottom-16 left-0 right-0 mx-8 sm:mx-16 z-50 pointer-events-none">
            <div className="f-toaster grid f-grid auto-rows-auto justify-items-center justify-center mx-auto pointer-events-none relative">
                <section className="overflow-hidden w-full" aria-label={ariaLabel}>
                    <div className={`toast flex group p-8 mt-16 rounded-8 border-2 w-full pointer-events-auto ${containerClass}`}>
                        <div className={`flex-shrink-0 rounded-full w-16 h-16 m-8 ${iconClass}`}>
                            <svg
                                role="img"
                                aria-label={ariaLabel}
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="none"
                                viewBox="0 0 16 16">
                                <path
                                    stroke="currentColor"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="1.5"
                                    d="M5.5 9l2 1.5L11 6"
                                />
                            </svg>
                        </div>
                        <div role={role} className="self-center mr-8 py-4 last-child:mb-0">
                            <p data-testid="toastMessage">{toast.message}</p>
                        </div>
                    </div>
                </section>
            </div>
        </aside>
    ) : null;
};
